
/**
 * Prayer Viewer Hook for Congregant Prayer Browsing
 *
 * This hook powers the public prayer viewing experience where congregants
 * browse through prayers during services. It integrates with the even
 * distribution algorithm to ensure fair prayer coverage.
 *
 * Features:
 *   - Fetches prayers using even distribution algorithm
 *   - Tracks views per device (anonymous, no login required)
 *   - Real-time notifications when items are added to the live service queue
 *   - Device ID persistence for view tracking
 *
 * Distribution Algorithm:
 *   1. Pool is only items on the live service queue (prayer + praise)
 *   2. First shows unviewed items for this device; then least-scored
 *   3. Ensures fair coverage within that pool
 *
 * @example
 * function PrayerViewerPage() {
 *   const {
 *     currentPrayer,
 *     isLoading,
 *     fetchNextPrayer,
 *     hasNewPrayers
 *   } = usePrayerViewer();
 *
 *   return (
 *     <div>
 *       {currentPrayer && <PrayerCard prayer={currentPrayer} />}
 *       <button onClick={fetchNextPrayer}>Next Prayer</button>
 *       {hasNewPrayers && <Badge>New prayers available!</Badge>}
 *     </div>
 *   );
 * }
 *
 * @module hooks/usePrayerViewer
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PrayerRequest } from '@/types';
import { api } from '@/lib/api';
import { getOrCreateDeviceId } from '@/lib/utils';
import { useWebSocket, WSEventType, WSMessage } from './useWebSocket';

/**
 * Options for the prayer viewer hook.
 */
export interface UsePrayerViewerOptions {
  /** Enable WebSocket for real-time "new prayers" notifications */
  enableRealtime?: boolean;
  /** Callback when new prayers become available */
  onNewPrayerAvailable?: () => void;
}

/**
 * Return type for the prayer viewer hook.
 */
export interface UsePrayerViewerReturn {
  /** The prayer currently being displayed (null if none) */
  currentPrayer: PrayerRequest | null;
  /** Whether a prayer is being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** True if new prayers arrived via WebSocket */
  hasNewPrayers: boolean;
  /** Count of prayers viewed this session */
  prayersViewed: number;
  /** Advances to the next prayer using distribution algorithm */
  fetchNextPrayer: () => Promise<void>;
  /** Clears the "new prayers available" notification */
  clearNewPrayersNotification: () => void;
  /** This device's unique identifier (for view tracking) */
  deviceId: string;
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** True when the server says this device hit its view limit */
  isViewLimitReached: boolean;
  /** Seconds remaining in cooldown (0 if no cooldown) */
  cooldownSecondsRemaining: number;
  /** True if button should be disabled due to pacing cooldown */
  isPacingCooldownActive: boolean;
  /** True if we should show a gentle reminder about taking time to pray */
  shouldShowPacingReminder: boolean;
}

/**
 * Hook for managing the congregant prayer viewing experience.
 *
 * Provides prayer fetching with even distribution, view tracking,
 * and real-time notifications for new prayers.
 *
 * @param options - Configuration options
 * @returns Prayer viewer state and controls
 */
// Minimum time between prayer views (30 seconds)
const MIN_TIME_BETWEEN_VIEWS_MS = 30 * 1000;
// Number of speed violations before enforcing timer (grace period)
const SPEED_VIOLATION_THRESHOLD = 10;

export function usePrayerViewer(options: UsePrayerViewerOptions = {}): UsePrayerViewerReturn {
  const { enableRealtime = true, onNewPrayerAvailable } = options;

  // State
  const [currentPrayer, setCurrentPrayer] = useState<PrayerRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNewPrayers, setHasNewPrayers] = useState(false);
  const [prayersViewed, setPrayersViewed] = useState(0);
  const [deviceId, setDeviceId] = useState('');
  const [isViewLimitReached, setIsViewLimitReached] = useState(false);
  const [cooldownSecondsRemaining, setCooldownSecondsRemaining] = useState(0);
  const [shouldShowPacingReminder, setShouldShowPacingReminder] = useState(false);

  const applyViewsTodayHeader = useCallback((headers?: Record<string, string>) => {
    const raw = headers?.['X-Views-Today'];
    if (raw === undefined) return;
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) {
      setPrayersViewed(n);
    }
  }, []);

  // Refs
  const isInitializedRef = useRef(false);
  const onNewPrayerAvailableRef = useRef(onNewPrayerAvailable);
  const lastViewTimestampRef = useRef<number | null>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const speedViolationCountRef = useRef(0);
  const reminderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep callback ref updated
  useEffect(() => {
    onNewPrayerAvailableRef.current = onNewPrayerAvailable;
  }, [onNewPrayerAvailable]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
      if (reminderTimeoutRef.current) {
        clearTimeout(reminderTimeoutRef.current);
      }
    };
  }, []);

  // Update cooldown timer every second
  useEffect(() => {
    if (cooldownSecondsRemaining > 0) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldownSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (cooldownTimerRef.current) {
              clearInterval(cooldownTimerRef.current);
              cooldownTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    }

    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, [cooldownSecondsRemaining]);

  // Initialize device ID on mount (client-side only)
  useEffect(() => {
    const id = getOrCreateDeviceId();
    setDeviceId(id);
  }, []);

  // WebSocket for real-time updates
  const { isConnected, reconnectedTick } = useWebSocket({
    rooms: ['public'],
    autoReconnect: enableRealtime,
    onMessage: (message: WSMessage) => {
      // Public pool grew (distinct from staff-only prayer_queued full queue rows)
      if (message.event === WSEventType.LIVE_QUEUE_ITEM_ADDED_PUBLIC) {
        setHasNewPrayers(true);
        onNewPrayerAvailableRef.current?.();
      }
    },
  });

  useEffect(() => {
    if (!enableRealtime || reconnectedTick <= 0) return;
    setHasNewPrayers(true);
    onNewPrayerAvailableRef.current?.();
  }, [reconnectedTick, enableRealtime]);

  useEffect(() => {
    if (!enableRealtime || isConnected) return;
    const id = setInterval(() => {
      setHasNewPrayers(true);
      onNewPrayerAvailableRef.current?.();
    }, 15_000);
    return () => clearInterval(id);
  }, [enableRealtime, isConnected]);

  /**
   * Fetch the next prayer from the server
   */
  const fetchNextPrayer = useCallback(async () => {
    if (!deviceId) return;
    if (isViewLimitReached) return;
    
    // Check pacing: if viewing too quickly, track violations and enforce cooldown after threshold
    const now = Date.now();
    if (lastViewTimestampRef.current !== null) {
      const timeSinceLastView = now - lastViewTimestampRef.current;
      if (timeSinceLastView < MIN_TIME_BETWEEN_VIEWS_MS) {
        // Increment violation count
        speedViolationCountRef.current += 1;
        
        // Show gentle reminder after first violation
        if (speedViolationCountRef.current === 1) {
          setShouldShowPacingReminder(true);
          // Hide reminder after 5 seconds
          if (reminderTimeoutRef.current) {
            clearTimeout(reminderTimeoutRef.current);
          }
          reminderTimeoutRef.current = setTimeout(() => {
            setShouldShowPacingReminder(false);
          }, 5000);
        }
        
        // Only enforce timer after exceeding threshold
        if (speedViolationCountRef.current >= SPEED_VIOLATION_THRESHOLD) {
          // Calculate remaining cooldown time
          const remainingMs = MIN_TIME_BETWEEN_VIEWS_MS - timeSinceLastView;
          const remainingSeconds = Math.ceil(remainingMs / 1000);
          setCooldownSecondsRemaining(remainingSeconds);
          return; // Block the request
        }
        // Before threshold: allow but show reminder
      } else {
        // User waited appropriately - reset violation count
        speedViolationCountRef.current = 0;
        setShouldShowPacingReminder(false);
        if (reminderTimeoutRef.current) {
          clearTimeout(reminderTimeoutRef.current);
          reminderTimeoutRef.current = null;
        }
      }
    }
    
    // Block if cooldown is active (only after threshold)
    if (cooldownSecondsRemaining > 0) return;

    setIsLoading(true);
    setError(null);

    try {
      // Record view of current prayer if exists (must complete before fetching next)
      if (currentPrayer) {
        try {
          const viewRes = await api.post(
            `/prayer-requests/${currentPrayer.id}/view?device_id=${deviceId}`,
            undefined,
            { auth: false }
          );
          applyViewsTodayHeader(viewRes.responseHeaders);
          lastViewTimestampRef.current = Date.now();
        } catch (viewError) {
          console.warn('Failed to record prayer view:', viewError);
        }
      }

      // Small delay to ensure view is committed before fetching next
      // This helps avoid race conditions where the view hasn't been persisted yet
      await new Promise(resolve => setTimeout(resolve, 100));

      // Fetch next unviewed prayer (after view is recorded and committed)
      const response = await api.get<PrayerRequest>(
        `/prayer-requests/next?device_id=${deviceId}`,
        { auth: false }
      );

      applyViewsTodayHeader(response.responseHeaders);

      if (response.error) {
        if (response.status === 429) {
          setIsViewLimitReached(true);
          setError(response.error);
        } else {
          setError(response.error);
          setCurrentPrayer(null);
        }
      } else if (response.status === 204 || !response.data) {
        setCurrentPrayer(null);
        setError(null);
      } else if (response.data) {
        setCurrentPrayer(response.data);
        setError(null);
        setHasNewPrayers(false);
        setIsViewLimitReached(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unable to load prayers. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentPrayer, deviceId, isViewLimitReached, cooldownSecondsRemaining, applyViewsTodayHeader]);

  /**
   * Clear the new prayers notification
   */
  const clearNewPrayersNotification = useCallback(() => {
    setHasNewPrayers(false);
  }, []);

  // Fetch initial prayer when device ID is ready
  useEffect(() => {
    if (deviceId && !isInitializedRef.current) {
      isInitializedRef.current = true;
      fetchNextPrayer();
    }
  }, [deviceId, fetchNextPrayer]);

  const isPacingCooldownActive = cooldownSecondsRemaining > 0;

  return {
    currentPrayer,
    isLoading,
    error,
    hasNewPrayers,
    prayersViewed,
    fetchNextPrayer,
    clearNewPrayersNotification,
    deviceId,
    isConnected,
    isViewLimitReached,
    cooldownSecondsRemaining,
    isPacingCooldownActive,
    shouldShowPacingReminder,
  };
}

export default usePrayerViewer;
